// Flow 1 - Money view (docs/phase-0/slice6-ux-flows.md §3; slice6-component-designs.md §3).
// The `/apps/:appId/money` AppDetail sub-nav tab - reads `useOutletContext<AppDetailContext>()`
// exactly as PulseTab/AppExplained do (UX §2.1), no new route guard beyond the inherited
// cross-tenant gate. This slice renders the READ-ONLY money view (AC-1): the all-in cost broken
// down ONLY by cloud/services/nightcrew-plan, the 3-state P&L (computed / partial / abstain -
// never a fabricated net), and the plan-capacity gauge (changes shipped + the one monthly-limit
// dollar figure, D48). D27: no per-fix/per-action/token figure appears anywhere - the wire MoneyView
// is a z.strictObject that cannot carry one. The billing-inbox add form + monthly-limit dialog reuse
// MoneyPanel and are a follow-up; the read view is the load-bearing AC.
//
// The plan-capacity panel's WORDS come from `../../copy/money.js`, which reproduces
// `crew/config/plan-language.json` under D98 (see that file's own header). What matters here: the
// countable thing is a CHANGE, the word "overage" never renders even though the wire field is
// `overageUnits`, and the founder's guardrail renders as a monthly LIMIT even though the wire field
// is `monthlyCeilingMinor`. The schema names are frozen; only what a founder reads is D98's.
//
// AND `monthlyCeilingMinor` IS NOT EUR. Two comments in this file said it was, and both survived
// ADR-0039 removing the conversion they justified until a verification panel found them. It holds
// minor units of the account's PLAN currency, USD for every shipped plan, so this screen renders it
// unconverted.
//
// TWO COUNTS OF EXTRA CHANGES REACH THIS PANEL AND THEY ARE NOT INTERCHANGEABLE. `overageUnits` is
// the work past the allowance (`usage_counters.overage_units`); `billedExtraChanges` is the count the
// invoice charges for after D98 clause 4 caps the month at what a tier above would have cost. They
// are equal until the cap binds and diverge after it. The rule this panel enforces: wherever a count
// sits next to the extra-change PRICE, it is `billedExtraChanges`, because the founder multiplies.
import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { isErr, selectGaugeState } from "@nightcrew/shared";
import type { MoneyView } from "@nightcrew/shared";
import type { AppDetailContext } from "../AppDetail.js";
import { getMoneyView, keepMonthlyLimit, setMonthlyLimit } from "../../api/money.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { MoneyPanel } from "./MoneyPanel.js";
import { moneyCopy } from "../../copy/money.js";
import { formatUsdMinor } from "./format.js";

type MoneyLoad =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly view: MoneyView };

/**
 * The one-tap adjust dialog's own state (AC3 clause 2, D48).
 *
 * `saving` and `failed` are SEPARATE states rather than one enum with an error string, because the
 * dialog must stay open and keep the founder's typed value on a failure - closing it or clearing the
 * field would make a transient network error cost them the number they just chose.
 */
type LimitDialog =
  | { readonly status: "closed" }
  | { readonly status: "open"; readonly value: string; readonly saving: boolean; readonly failed: boolean };

// EVERY figure below goes through `formatUsdMinor`, and every input to it is a category rollup, a
// plan-level aggregate, or a per-change PRICE - never a per-fix, per-action or per-token cost (D27;
// a price the customer is charged is allowed, a cost is not: money-view.ts:74-80). This screen used
// to carry its own `` usd = `$${(minor / 100).toFixed(2)}` ``, a second and worse implementation of
// a helper its own module already exported: it printed `$NaN` on a non-finite amount where
// `formatMinor` falls back to something readable (format.ts:20-23,44-51), and it dropped the
// thousands separator Intl supplies. Deleted rather than kept beside it.

const categoryLabel = (category: "cloud" | "services" | "nightcrew_plan"): string =>
  category === "nightcrew_plan"
    ? moneyCopy("money.allInCost.nightcrewPlanNote")
    : moneyCopy(`money.allInCost.category.${category}`);

export const MoneyTab = (): React.JSX.Element => {
  const { app } = useOutletContext<AppDetailContext>();
  const { state: session } = useSession();
  // AC3 clause 2 is owner/admin only, and the SERVER is what enforces that - `@Roles` at the method,
  // with RolesGuard reading the DB-authoritative role. This is the UI half of the same decision: a
  // viewer is told why rather than shown a control that will refuse them, which is the pattern
  // `AppExplained.tsx:194` already establishes. It is not a second access decision.
  const canManage = isOwnerOrAdmin(session);
  const [load, setLoad] = useState<MoneyLoad>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [dialog, setDialog] = useState<LimitDialog>({ status: "closed" });
  // The acknowledgement is local because it is about THIS interaction: the server has recorded the
  // answer, and the re-read below will stop `limitPromptPending` for good. This only decides whether
  // the founder sees "Left at X" before that read returns.
  const [promptKept, setPromptKept] = useState<number | null>(null);
  const changeTriggerRef = useRef<HTMLButtonElement>(null);

  const saveLimit = async (raw: string): Promise<void> => {
    // Parsed as DOLLARS, stored as minor units of the same currency. NO FX: `apps.monthly_ceiling_minor`
    // holds the plan's currency (ADR-0039), which is what the founder is typing.
    const dollars = Number(raw.trim());
    const minor = Math.round(dollars * 100);
    if (!Number.isFinite(dollars) || !Number.isInteger(minor) || minor <= 0) {
      setDialog({ status: "open", value: raw, saving: false, failed: true });
      return;
    }
    setDialog({ status: "open", value: raw, saving: true, failed: false });
    const result = await setMonthlyLimit(app.id, minor);
    if (isErr(result)) {
      // THE DIALOG STAYS OPEN AND KEEPS THE TYPED VALUE. A transient failure must not cost the
      // founder the number they just chose.
      setDialog({ status: "open", value: raw, saving: false, failed: true });
      return;
    }
    setDialog({ status: "closed" });
    // Re-read rather than patching local state: the limit is one field of a view whose other figures
    // (the gauge, the captions) are derived server-side, and a local patch would leave them stale.
    setAttempt((n) => n + 1);
  };

  useEffect(() => {
    const cancelled = { current: false };
    setLoad({ status: "loading" });
    void (async () => {
      const result = await getMoneyView(app.id);
      if (cancelled.current) return;
      setLoad(isErr(result) ? { status: "error" } : { status: "ready", view: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [app.id, attempt]);

  if (load.status === "loading") {
    return (
      <div className="wrap">
        <h1 className="h2">{app.name}</h1>
        <div className="card pad skeleton-card mt16" role="status" aria-label={moneyCopy("money.tab.title")} />
      </div>
    );
  }

  if (load.status === "error") {
    return (
      <div className="wrap">
        <h1 className="h2">{app.name}</h1>
        <div className="card state-card mt16">
          <p>{moneyCopy("money.error.title")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAttempt((a) => a + 1);
            }}
          >
            {moneyCopy("money.error.retryCta")}
          </button>
        </div>
      </div>
    );
  }

  const { view } = load;
  const { allInCost, pnl, planCapacity } = view;
  // NO FX CONVERSION, and its removal is the point (ADR-0039). This line used to read
  // `eurMinorToUsdMinor(planCapacity.monthlyCeilingMinor)` on the belief that the column held EUR
  // cents. It does not: `apps.monthly_ceiling_minor` holds minor units of the account's PLAN
  // currency, which is USD for every shipped plan. Converting a USD figure as though it were EUR
  // OVERSTATED the founder's own guardrail by 1/0.855 = 1.1696 - a $500 limit rendered as $584.80.
  // The number is already in the unit this screen displays, so the honest transformation is none.
  const limitUsdMinor = planCapacity.monthlyCeilingMinor;

  return (
    <div className="wrap">
      <h1 className="h2">{app.name}</h1>

      {/* All-in cost - cloud / services / nightcrew-plan ONLY (D27) */}
      <h2 className="h3 mt16">{moneyCopy("money.allInCost.heading")}</h2>
      <div className="card pad mt8" data-testid="money-all-in-cost">
        {allInCost.byCategory.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            {moneyCopy("money.allInCost.emptyCategory")}
          </p>
        ) : (
          allInCost.byCategory.map((line) => (
            <div key={line.category} className="control-row">
              <span>{categoryLabel(line.category)}</span>
              <strong>{formatUsdMinor(line.amountMinor)}</strong>
            </div>
          ))
        )}
        <div className="control-row" style={{ borderTop: "1px solid var(--ink-4)", marginTop: 8, paddingTop: 8 }}>
          <span>{moneyCopy("money.allInCost.total")}</span>
          <strong>{formatUsdMinor(allInCost.totalMinor)}</strong>
        </div>
      </div>

      {/* Profit & loss - 3-state, abstains rather than fabricate a net */}
      <h2 className="h3 mt24">{moneyCopy("money.pnl.heading")}</h2>
      <div className="card pad mt8" data-testid="money-pnl">
        {pnl.kind === "computed" ? (
          <p style={{ margin: 0 }}>
            <span className={`status-chip ${pnl.netMinor >= 0 ? "good" : "risk"}`}>
              {pnl.netMinor >= 0 ? moneyCopy("money.pnl.computed.profit") : moneyCopy("money.pnl.computed.loss")}
            </span>{" "}
            {moneyCopy("money.pnl.computed.line", {
              revenue: formatUsdMinor(pnl.revenueMinor),
              cost: formatUsdMinor(pnl.allInCostMinor),
              net: formatUsdMinor(Math.abs(pnl.netMinor)),
            })}
          </p>
        ) : pnl.kind === "partial" ? (
          <p className="muted" style={{ margin: 0 }} data-testid="money-pnl-partial">
            {pnl.revenueMinor !== undefined
              ? moneyCopy("money.pnl.partial.revenueKnown", { revenue: formatUsdMinor(pnl.revenueMinor) })
              : moneyCopy("money.pnl.partial.revenueMissing")}{" "}
            {pnl.allInCostMinor !== undefined
              ? moneyCopy("money.pnl.partial.costKnown", { cost: formatUsdMinor(pnl.allInCostMinor) })
              : moneyCopy("money.pnl.partial.costMissing")}
          </p>
        ) : (
          <p className="muted" style={{ margin: 0 }} data-testid="money-pnl-abstain">
            <span className="status-chip neutral">{moneyCopy("money.pnl.insufficientData.chip")}</span>{" "}
            {pnl.reason}
          </p>
        )}
      </div>

      {/* Plan capacity - changes shipped + the one monthly-limit dollar figure (D48). NEVER a
          per-fix cost meter, and never `.meter.over`'s red fill: working past the included changes
          is the product functioning, so the tone stays calm (component-designs §3.4's own note). */}
      <h2 className="h3 mt24">{moneyCopy("money.planCapacity.heading")}</h2>
      <div className="card pad mt8" data-testid="money-plan-capacity">
        <p className="muted" style={{ marginTop: 0 }} data-testid="money-capacity-gauge-caption">
          {/* ABSTAIN RATHER THAN QUOTE A PRICE WE DO NOT HAVE. The over-line is the only place a
              founder reads the extra-change PRICE, and every sanctioned form of it requires `{rate}`
              (crew/config/plan-language.json strings.gauge_over) - there is deliberately no "over,
              price unknown" wording, because a plan that charges for extra changes always knows what
              it charges. So `overagePriceMinor <= 0` is not a price of zero, it is the ABSENCE of a
              resolved price: money.service.ts fails closed to 0 when the account has no subscription
              row it can join to a plan. Rendering it anyway printed "Extra changes are $0.00 each" to
              a founder who is being charged, which invents a high-stakes figure (section 0 rule 4) and
              invents the most damaging one available. Falling back to the healthy caption states only
              what is known; the extra count still shows in the chip below, so nothing is hidden.

              A ZERO `billedExtraChanges` ABSTAINS FOR THE SAME REASON, and it is the ordinary state of
              a fully capped month rather than an error. The sanctioned sentence needs a count AND a
              price, and "0 more than your plan includes. Extra changes are $12.50 each" states a
              charge that does not exist. Two real inputs produce it: no resolved rate (a rate of zero
              charges for nothing, `billOnRung` at allowance.ts:474), and a cap that holds the month to
              the bare subscription. In both the founder owes the plan price and nothing beyond it, so
              the panel says nothing beyond it - the healthy caption plus the chip's raw count, which
              is the honest pair: this much work happened, no extra charge is claimed. */}
          {/* THE COUNT BESIDE THE RATE IS THE BILLED COUNT, NEVER THE RAW EXCESS. This one sentence
              states a count and a price with "each" between them, so a founder multiplies it out -
              and past the D98 upgrade crossover the raw excess is larger than what is billed. At 45
              changes on Solo the raw excess is 33 and the invoice charges for 12, because the month
              is held to what Studio would have cost: 33 x $12.50 reads as $412.50 against a real
              extra charge of $150 inside a $249 total. Rendering `overageUnits` here quoted a founder
              a figure 2.75x what they owe, which is a section 0 rule 4 defect on a shipped surface.
              `billedExtraChanges` carries the identity
              `billedMinor === plan.priceMinor + billedExtraChanges * plan.overagePriceMinor`
              (allowance.ts:431-441, property-tested), so the rendered count times the rendered rate
              IS the extra-change charge. `overageUnits` is kept on the wire and still shown in the
              chip below: it is what `usage_counters.overage_units` holds and the honest count of work
              past the allowance, which is a different question from what the month costs. */}
          {/* AND WHEN THE CAP BINDS, A THIRD CAPTION STATES A TOTAL INSTEAD OF A RATE (ADR-0031).
              `gauge_over` uses ONE number for TWO jobs: "{extra} more than your plan includes", which
              is a claim about WORK and is true only of the raw excess, and "extra changes are ${rate}
              each", which invites a multiplication and is true only of the billed count. Uncapped
              those are the same number and the sentence is fine. Capped they diverge, and an
              adversarial panel proved BOTH readings ship a section 0 rule 4 defect: rendering the raw
              33 quotes $412.50 against a $150 charge, and rendering the billed 12 says "12 more than
              your plan includes" to a founder who shipped 33 more, while the chip directly below says
              "+33 more this month" and contradicts it.
              There is no third COUNT, so the fix is to stop stating a rate: `gaugeOverCapped` renders
              the raw excess (its only true meaning, so it agrees with the chip) beside the month's
              TOTAL, which has no second reading and cannot be multiplied into a figure the founder
              will not be sent. `billedMinor` carries the identity's left-hand side from the same
              single run of the cap that produced the count (money.service.ts, `monthlyBillFor`).
              THE ABSTAIN REMAINS THE FALLBACK, and it is still reachable: `billedMinor === 0` is the
              ABSENCE of a resolved total rather than a free month (no subscription row, or a ladder
              that could not be read whole), so a zero renders the healthy caption instead of stating
              "capped this month at $0.00". Same rule as the unresolved rate below it.

              AND THE CLAUSE READS `capReliefMinor`, NOT "THE TWO COUNTS DIVERGED". A panel refuted the
              first version of this gate, which used `billedExtraChanges !== overageUnits` on the
              reasoning that the counts only diverge once the cap binds. They ALSO diverge when the
              plan's extra-change rate is zero, which the withdrawn rungs in `plans` carry (the explicit
              not-set escape 0022's CHECK allows). Executed: withdrawn `solo` 4999/100/0 at 140 changes
              gives `cappedByRung null, capReliefMinor 0, billedExtraChanges 0` against an
              `overageUnits` of 40 - and the caption claimed "capped this month at $49.99, so you never
              pay more than the next plan up" about a month where NOTHING capped. Inferring a fact about
              the customer's bill is how you fabricate one; `capReliefMinor > 0` is the fact itself. */}
          {planCapacity.overageUnits > 0 &&
          planCapacity.billedExtraChanges === planCapacity.overageUnits &&
          planCapacity.overagePriceMinor > 0
            ? moneyCopy("money.planCapacity.gaugeOver", {
                used: planCapacity.valueUnitsUsed,
                extra: planCapacity.billedExtraChanges,
                // A PRICE the founder is charged per extra change, not a cost (money-view.ts:74-80).
                // Giving it is the whole point of the line: the invoicing word is gone, so the honest
                // replacement is the number (ux spec §3.4).
                rate: formatUsdMinor(planCapacity.overagePriceMinor),
              })
            : planCapacity.capReliefMinor > 0 &&
                planCapacity.billedMinor > 0 &&
                planCapacity.overageUnits > 0 &&
                /* AND THE PROVIDER MUST NOT BE CARRYING A DISCOUNT (AC2, ADR-0032). This is the ONE
                   caption on this panel that states a MONTH TOTAL - "we have capped this month at
                   {amount}" - and `billedMinor` is Nightcrew arithmetic computed as though no
                   discount existed. Stripe is merchant of record (D74) and issues the invoice, so on
                   a discounted month the founder is sent a DIFFERENT number than this one, and
                   stating ours would be a figure the system cannot back said to the person paying it.
                   Nothing here knows the discount's SIZE, so there is no corrected total to state
                   instead; the honest move is to say nothing about the total and let the caption fall
                   through to the counts. Same abstain shape as `billedMinor === 0` above, and the
                   same reason: a total we cannot back is worse than no total. */
                !planCapacity.firstMonthDiscountByProvider &&
                /* AND THE TWO HALVES OF THE SENTENCE MUST COME FROM THE SAME ALLOWANCE. `{extra}` is
                   the counter's `overage_units`, which the writer stores as
                   `max(0, valueUnitsUsed - valueUnitsIncluded)` (usage-drawdown.service.ts:76-85).
                   `{amount}` is ladder arithmetic, and `monthlyBillFor` deliberately SUBSTITUTES the
                   plan's own `included_units` when `valueUnitsIncluded` is 0, because that column
                   defaults to 0 and a literal 0 would make every change a billed extra
                   (money.service.ts, clause 2 of its note - the punitive direction guards.ts forbids).
                   That substitution is right for the bill and it means the two figures can be measured
                   against DIFFERENT allowances: at used 45 / included 0 the counter says 45 past the
                   allowance while the bill was computed against 12, so the caption would say "45 more
                   than your plan includes" about a plan that includes 12. Requiring a populated
                   allowance costs nothing on a real capped month (12, 45 or 120) and removes the
                   mismatch instead of hoping it stays unreachable. */
                planCapacity.valueUnitsIncluded > 0
              ? moneyCopy("money.planCapacity.gaugeOverCapped", {
                  used: planCapacity.valueUnitsUsed,
                  // THE RAW EXCESS IS CORRECT HERE and the billed count would be wrong. This clause
                  // states no rate, so there is nothing to multiply, and the sentence's only claim is
                  // about work done - which is `overageUnits`, matching the chip below.
                  extra: planCapacity.overageUnits,
                  // NO FX CONVERSION, for the same reason `overagePriceMinor` two clauses up gets
                  // none: `billedMinor` is built from `plans.price_minor` and
                  // `plans.overage_price_minor`, which `crew/model/emit_seed.py` writes in USD minor
                  // (9900 is $99.00). NO FIGURE ON THIS SCREEN IS STORED IN EUR - this comment used
                  // to name `monthlyCeilingMinor` as the one that was, which ADR-0039 disproved.
                  amount: formatUsdMinor(planCapacity.billedMinor),
                })
              : /* THE THIRD STATE, and the reason it took an ADR to render four words. `gauge_near`
                   is `gauge_healthy` plus "Plenty of time left", which is a claim about the CALENDAR
                   rather than about the count, so it may only render where the calendar backs it
                   (ADR-0038). The rule lives in `selectGaugeState` beside the wire model both sides
                   read, not here: this branch chooses a string, it does not own a threshold.
                   ONLY near-versus-healthy is asked of it. The two clauses above stay exactly as
                   ADR-0031 left them, because the over state on this panel is not "used > included"
                   but a set of conditions about which COUNT may sit beside a rate - a question the
                   selector deliberately knows nothing about. Feeding it those clauses would have
                   re-decided ADR-0031 by accident. */
                selectGaugeState({
                    used: planCapacity.valueUnitsUsed,
                    included: planCapacity.valueUnitsIncluded,
                    /* A null fraction is an unknown period, and NaN is what the selector's finite
                       guard rejects, so an unknown month renders the caption that claims no time. */
                    periodElapsedFraction: planCapacity.periodElapsedFraction ?? Number.NaN,
                  }) === "near"
                ? moneyCopy("money.planCapacity.gaugeNear", {
                    used: planCapacity.valueUnitsUsed,
                    included: planCapacity.valueUnitsIncluded,
                  })
                : moneyCopy("money.planCapacity.gaugeHealthy", {
                    used: planCapacity.valueUnitsUsed,
                    included: planCapacity.valueUnitsIncluded,
                  })}
        </p>
        <div className="meter bordered" aria-hidden="true">
          <span
            style={{
              width: `${String(
                planCapacity.valueUnitsIncluded > 0
                  ? Math.min(100, Math.round((planCapacity.valueUnitsUsed / planCapacity.valueUnitsIncluded) * 100))
                  : 0,
              )}%`,
            }}
          />
        </div>
        {/* THE CHIP CARRIES THE RAW COUNT, AND CARRIES NO MONEY, which is what makes that correct.
            `overageUnits` is the work: what `usage_counters.overage_units` holds, changes the crew
            actually shipped past the allowance. Nothing here multiplies it by anything, so it cannot
            quote a charge, and it is the one line that survives when the caption abstains - the reason
            an unresolved price does not silently hide a busy month. The caption above deliberately
            renders a DIFFERENT number once the cap binds, because it is answering a different
            question: this chip says how much work happened, the caption says what is charged for.
            Those two figures separate the moment the cap gives work away, and the capped caption states
            the RAW count for exactly that reason, so the two agree (ADR-0031). The register now carries
            `gauge_over_capped` for that month; the sentence above describing it as absent was true when
            written and was left stale by the same commit that added the string, which a panel caught. */}
        {planCapacity.overageUnits > 0 ? (
          <span className="status-chip neutral mt8" style={{ display: "inline-flex" }}>
            {moneyCopy("money.planCapacity.extraChangesChip", { extra: planCapacity.overageUnits })}
          </span>
        ) : null}
        {/* `extra_changes_capped_note` (ADR-0031), rendered on the capped month and nowhere else. It
            was sanctioned in the register and then carried by no surface, which a panel raised as its
            own defect: a sanctioned string nothing renders is a claim about the product that nothing
            enforces. It states the cap's benefit in plain words beside the caption that states the
            total, and it quotes no rate, for the same reason the caption does not. Gated on the SAME
            condition as the capped caption so the two cannot appear apart. */}
        {planCapacity.capReliefMinor > 0 &&
        planCapacity.billedMinor > 0 &&
        planCapacity.overageUnits > 0 &&
        planCapacity.valueUnitsIncluded > 0 ? (
          <p className="muted mt8" style={{ marginBottom: 0 }} data-testid="money-capacity-capped-note">
            {moneyCopy("money.planCapacity.extraChangesCappedNote")}
          </p>
        ) : null}
        {/* D99: the only surface where a founder sees what the platform absorbed. A count, never a
            cost - `absorbedUnits` is an integer on the wire and no money figure accompanies it. */}
        {planCapacity.absorbedUnits > 0 ? (
          <p className="muted mt8" style={{ fontSize: 12.5, marginBottom: 0 }} data-testid="money-rework-free">
            {moneyCopy("money.planCapacity.reworkFree")}
          </p>
        ) : null}
        <p className="muted mt8" style={{ fontSize: 12.5, marginBottom: 0 }} data-testid="money-monthly-limit">
          {/* NO CONVERSION. This block previously converted the stored figure out of EUR, on a
              comment that said `monthlyCeilingMinor` is "EUR minor units". ADR-0039 settled that it
              is minor units of the PLAN'S currency, which is USD for every shipped plan, so the
              conversion overstated the founder's own guardrail by 1.1696 - a $500 limit rendered as
              $584.80. `formatUsdMinor` still fails closed on a non-finite amount, which `MinorUnits`
              (z.int()) cannot deliver anyway. */}
          {limitUsdMinor === null
            ? moneyCopy("money.planCapacity.noLimit")
            : moneyCopy("money.planCapacity.limitLabel", { amount: formatUsdMinor(limitUsdMinor) })}
        </p>
        {/* AC3'S PROMISE - AND THE SUBJECT OF ITS VERB IS LOAD-BEARING, which the first version of
            this block got wrong. `ceiling_line` reads "YOU HAVE SET a monthly limit of {amount}", and
            it rendered on the single condition that a limit EXISTS. Since AC3 clause 1 AUTO-SETS a
            limit from the plan on every new app, the panel told a founder they had set a figure the
            system chose for them. The comment here reasoned about the amount slot and never about who
            the sentence credits. A verification panel caught it, and WHY THE SUITE WAS GREEN is worth
            recording: every existing case in MoneyTab.test.tsx pinned `ceilingSource: "founder"`.

            THE FACT WAS ALREADY ON THE WIRE AND SIMPLY NOT READ. `planCapacity.ceilingSource` is
            `auto` or `founder`, and the catalog already held the right string for the other case
            (`limitSource.auto`, "Auto-set for your plan") with zero renderers.

            AND THE PROMISE ITSELF IS NOW WITHHELD, which is the SECOND panel finding on this block.
            `ceiling_line` ends "We never go past it without asking", and what enforces the pause
            (`shift/ceiling-resolver.ts:98-107`) compares this app's limit against the ACCOUNT's
            month of `run_ledger.cost_micro_eur` - internal model cost, on a table with no `app_id`
            column. Measured by a panel: two apps on one Studio account, and app B paused on app A's
            spend having used none of its own limit, while the paused app's own panel showed 44 of
            100 changes used and nothing billed past the plan. So the sentence names neither this
            app's spend nor the founder's bill. Both branches now state the SOURCE of the figure and
            nothing about what it does; `limitSource.founder` ("Set by you") was already sanctioned
            and already in the catalog. Restoring the promise is one line and it waits on CR-039:
            the owner either changes the enforcement to bound what the sentence names, or changes the
            sentence. The register keeps the owner's D48 wording untouched either way. */}
        {limitUsdMinor === null ? null : planCapacity.ceilingSource === "founder" ? (
          <p className="muted mt8" style={{ fontSize: 12.5, marginBottom: 0 }} data-testid="money-limit-source">
            {moneyCopy("money.planCapacity.limitSource.founder")}
          </p>
        ) : (
          <p className="muted mt8" style={{ fontSize: 12.5, marginBottom: 0 }} data-testid="money-limit-source">
            {moneyCopy("money.planCapacity.limitSource.auto")}
          </p>
        )}
        {/* AC3 CLAUSE 3: the keep-or-change prompt, "the first time it matters" (D48).
            WHETHER TO SHOW IT IS THE SERVER'S ANSWER, not a rule re-derived here. `limitPromptPending`
            already accounts for the month having reached the `near` band, a limit existing to keep,
            and this founder not having been asked before. Recomputing any of that in the client would
            be a second definition of "the first time it matters" that could disagree with the first.
            "Keep it" is a real answer rather than a dismissal: it POSTs, which records that the
            founder was asked and chose, and that record is what makes "we will not ask again for this
            app" true across a reload. */}
        {planCapacity.limitPromptPending && limitUsdMinor !== null && canManage ? (
          <div className="card pad mt8" data-testid="money-limit-prompt">
            <p style={{ fontWeight: 600, marginTop: 0 }}>
              {moneyCopy("money.planCapacity.limitPrompt.heading")}
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              {moneyCopy("money.planCapacity.limitPrompt.body", { amount: formatUsdMinor(limitUsdMinor) })}
            </p>
            {promptKept === null ? (
              <div>
                <button
                  type="button"
                  className="btn"
                  data-testid="money-limit-prompt-keep"
                  onClick={() => {
                    void (async () => {
                      const result = await keepMonthlyLimit(app.id);
                      if (!isErr(result)) {
                        setPromptKept(result.value.monthlyLimitMinor);
                        setAttempt((n) => n + 1);
                      }
                    })();
                  }}
                >
                  {moneyCopy("money.planCapacity.limitPrompt.keepCta")}
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  data-testid="money-limit-prompt-change"
                  onClick={() => {
                    setDialog({
                      status: "open",
                      value: (limitUsdMinor / 100).toFixed(2),
                      saving: false,
                      failed: false,
                    });
                  }}
                >
                  {moneyCopy("money.planCapacity.limitPrompt.changeCta")}
                </button>
              </div>
            ) : (
              <p className="muted" style={{ fontSize: 13, marginBottom: 0 }} data-testid="money-limit-prompt-ack">
                {moneyCopy("money.planCapacity.limitPrompt.keptAck", { amount: formatUsdMinor(promptKept) })}
              </p>
            )}
          </div>
        ) : null}
        {/* AC3 CLAUSE 2, THE FOUNDER-FACING HALF. The route landed with the API work and had no
            control anywhere in the UI, so "one-tap adjustable" was true of the server and false of
            the product. A viewer is told why instead of being shown a control that would refuse
            them - the server's `@Roles` is what actually enforces it. */}
        {canManage ? (
          <button
            type="button"
            className="btn ghost mt8"
            ref={changeTriggerRef}
            data-testid="money-limit-change"
            onClick={() => {
              setDialog({
                status: "open",
                // Pre-filled with the CURRENT limit so "one tap" is a nudge rather than a re-entry,
                // and empty when none is set rather than seeded with a number nobody chose.
                value: limitUsdMinor === null ? "" : (limitUsdMinor / 100).toFixed(2),
                saving: false,
                failed: false,
              });
            }}
          >
            {moneyCopy("money.planCapacity.changeCta")}
          </button>
        ) : (
          <p className="muted mt8" style={{ fontSize: 12.5, marginBottom: 0 }} data-testid="money-limit-denied">
            {moneyCopy("money.planCapacity.viewerDeniedReason")}
          </p>
        )}
        {dialog.status === "open" ? (
          <MoneyPanel
            heading={moneyCopy("money.planCapacity.limitDialog.heading")}
            onClose={() => {
              setDialog({ status: "closed" });
            }}
            triggerRef={changeTriggerRef}
            testId="money-limit-dialog"
          >
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (!dialog.saving) void saveLimit(dialog.value);
              }}
            >
              <label className="label" htmlFor="money-limit-input">
                {moneyCopy("money.planCapacity.limitDialog.fieldLabel")}
              </label>
              <input
                id="money-limit-input"
                className="input"
                type="number"
                min="0.01"
                step="0.01"
                inputMode="decimal"
                value={dialog.value}
                disabled={dialog.saving}
                onChange={(event) => {
                  setDialog({ status: "open", value: event.target.value, saving: false, failed: false });
                }}
              />
              <span className="muted" style={{ fontSize: 12.5 }}>
                {moneyCopy("money.planCapacity.limitDialog.currencySuffix")}
              </span>
              {dialog.failed ? (
                <p className="muted mt8" role="alert" data-testid="money-limit-error">
                  {moneyCopy("money.planCapacity.limitDialog.errorBody")}
                </p>
              ) : null}
              <div className="mt8">
                <button type="submit" className="btn" disabled={dialog.saving}>
                  {moneyCopy(
                    dialog.saving
                      ? "money.planCapacity.limitDialog.savingCta"
                      : "money.planCapacity.limitDialog.saveCta",
                  )}
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  disabled={dialog.saving}
                  onClick={() => {
                    setDialog({ status: "closed" });
                  }}
                >
                  {moneyCopy("money.planCapacity.limitDialog.cancelCta")}
                </button>
              </div>
            </form>
          </MoneyPanel>
        ) : null}
        {/* THE EXTRA-CHANGE PRICE, STATED CALMLY AND BEFORE IT COSTS ANYTHING. Also zero renderers
            until now. It is gated on a RESOLVED price for the same reason the over-line is: a 0 is
            the ABSENCE of a price (no subscription row to join a plan), not a price of zero, and
            "Extra changes are $0.00 each" is a claim about money that nothing backs.
            AND IT IS SUPPRESSED ONCE THE MONTH IS ALREADY OVER, because the caption above then
            states the same rate with a count attached and two rates on one panel invite the founder
            to add them. This is the calm-before version, not a duplicate. */}
        {planCapacity.overagePriceMinor > 0 && planCapacity.overageUnits === 0 ? (
          <p className="muted mt8" style={{ fontSize: 12.5, marginBottom: 0 }} data-testid="money-extra-changes-line">
            {moneyCopy("money.planCapacity.extraChangesLine", {
              rate: formatUsdMinor(planCapacity.overagePriceMinor),
            })}
          </p>
        ) : null}
        {/* ACCOUNTS FOR EVERY FIGURE ABOVE IT, which is why it sits last and is not gated on the
            over-line's conditions. A founding partner's price, extra-change rate and month total are
            all lower than the published ones; without this sentence the panel silently contradicts
            the pricing page and the founder has no way to tell a discount from a bug. It states no
            terms - the numbers on the panel already are the terms.

            IT STATES NO TERMS IS NOW TRUE. When this comment was written the string went on to say
            "These prices stay yours until we close the founding programme", which is a term, and a
            false one: `founding-rate.ts` stores the 30% RULE rather than a locked price, so a panel
            moved `plans.price` 9900 -> 14900 with the programme open and watched the founder's billed
            figure move 6930 -> 10430. The end-condition wording is section 8 owner copy (D57 sold
            price certainty) and is carried as CR-039. */}
        {planCapacity.foundingRateApplied ? (
          <p className="muted mt8" style={{ fontSize: 12.5, marginBottom: 0 }} data-testid="money-founding-rate-line">
            {moneyCopy("money.planCapacity.foundingRateApplied")}
          </p>
        ) : null}
      </div>
    </div>
  );
};
